import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '../typography/Typography';
import useTile from '../private/components/tile/useTile';

/**
 * A wrapper around `Typography` that renders a line-through decoration on disabled tiles.
 *
 * All of the `Typography` props are also available.
 *
 * Related components: [Tile](#tile), [TileContent](#tilecontent), [TileMedia](#tilemedia), [Typography](#typography).
 *
 * Usage:
 *
 * ```jsx
 * import { TileText } from '@one-thd/sui-atomic-components';
 * ```
 *
 */
const TileText = React.forwardRef((props, ref) => {
  const {
    children,
    decoration,
    height = 'tight',
    size = 'base',
    ...other
  } = props;

  const { selected, unavailable } = useTile();

  const color = unavailable ? 'medium' : 'primary';
  const variant = size === 'base' ? 'body-base' : 'body-sm';
  const weight = selected ? 'bold' : 'regular';

  return (
    <Typography
      ref={ref}
      color={color}
      decoration={decoration}
      height={height}
      variant={variant}
      weight={weight}
      {...other}
    >
      {children}
    </Typography>
  );

});

TileText.displayName = 'TileText';

TileText.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Applies the text decoration styles.
   */
  decoration: PropTypes.oneOf([
    'line-through',
    'underline'
  ]),
  /**
   * Applies the line height styles.
   */
  height: PropTypes.oneOf([
    'none',
    'tight',
    'snug',
    'normal',
    'loose'
  ]),
  /**
   * The size of the text component.
   * @default 'base'
   */
  size: PropTypes.oneOf(['base', 'small'])
};

export { TileText };
